// @ts-nocheck
import React, { useState, useEffect, useContext, useRef } from 'react';
import { colors, fontSizes, layout, fontFamily, shadows } from '@/theme';
import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';
import Helper from '@/utils/helper';
import PageContext from '@/utils/page-context';
import JobService from '@/services/job/job-service';
import AttachmentService from '@/services/attachment-service';
import NoSSR from '@/utils/no-ssr'
import SCInput from '@/components/sc-controls/form-controls/sc-input';
import Button from '@/components/button';
import SignatureCanvas from 'react-signature-canvas';
import useWindowSize from "@/hooks/useWindowSize";
import useMobileView from '@/hooks/useMobileView';
import ToastContext from '@/utils/toast-context';
import LeadMessage from '@/components/customer-zone/general/lead-message';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'

function Signature() {

    const router = useRouter();
    const { t, c, i } = router.query; // TODO(tanstack-router): verify router.query → useParams/useSearch

    const { data: tenantData } = useQuery(
        ['czTenant', t],
        () => Fetch.preLogin({ tenantID: t }),
        { enabled: !!t }
    );
    const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0 ? tenantData.Results[0] : null;

    const { data: jobData } = useQuery(
        ['czSignatureJob', t, c, i],
        () => JobService.getJobCustomerZone(i, c, t, tenant.API),
        { enabled: !!tenant }
    );

    const [job, setJob] = useState<any>(null);

    const pageContext = useContext(PageContext);

    useEffect(() => {
        if (tenantData && !tenant) {
            router.replace(`/customerzone/joblist?t=${t}&c=${c}`);
        }
    }, [tenantData]);

    useEffect(() => {
        if (jobData && !job) {
            setJob(jobData);
            (pageContext as any).updateTenantName(jobData.headerTenantName);
            if (jobData.Contact) {
                setContactName(jobData.Contact.FullName);
            }
            getCustomerSignature();
        }
    }, [jobData]);

    const windowSize = useWindowSize();
    const [mobileView] = useMobileView();

    const [contactName, setContactName] = useState('');

    const handleContactChange = (e: any) => {
        setContactName(e.value);
    };

    const [showSignatureOverlay, setShowSignatureOverlay] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);

    const signatureClick = () => {
        setShowSignatureOverlay(true);
    };

    const signatureRef = useRef<any>(null);
    const [signature, setSignature] = useState<any>();

    const clearSignature = () => {
        signatureRef.current.clear();
        setSignature(null);
    };

    const signatureCallback = (e: any) => {
        let temp = signatureRef.current.toDataURL().replace("data:image/png;base64,", "");
        setSignature(temp);
    };

    const [signatureCanvasWidth, setSignatureCanvasWidth] = useState(0);
    const [signatureCanvasHeight, setSignatureCanvasHeight] = useState(0);

    useEffect(() => {
        if (windowSize && showSignatureOverlay) {
            if (windowSize.height! > windowSize.width!) {
                setSignatureCanvasWidth(windowSize.width! - 25);
                setSignatureCanvasHeight(windowSize.height! - 300);
            } else {
                setSignatureCanvasWidth(windowSize.width! - 200);
                setSignatureCanvasHeight(windowSize.height! - 100);
            }
        }
    }, [showSignatureOverlay, windowSize]);

    const [signatureCardMaxHeight, setSignatureCardMaxHeight] = useState('unset');
    const [thumbnailMaxHeight, setThumbnailMaxHeight] = useState('unset');

    useEffect(() => {
        if (windowSize) {
            let newHeight = windowSize.height! - 130;
            setSignatureCardMaxHeight(`${newHeight}px`);
            setThumbnailMaxHeight(`${newHeight / 2}px`);
        }
    }, [windowSize]);

    const toast = useContext(ToastContext);
    const [inputErrors, setInputErrors] = useState<any>({});

    const [signatureAttachment, setSignatureAttachment] = useState<any>();
    const [hasSignatureAttachment, setHasSignatureAttachment] = useState(false);

    const getCustomerSignature = async () => {
        if (!tenant) return;
        let { customerSignature } = await AttachmentService.getSignaturesCustomerZone(c, t, tenant.API, i);
        if (customerSignature) {
            setSignatureAttachment(customerSignature);
            setHasSignatureAttachment(true);
        }
    };

    const validate = () => {
        let validationItems = [];

        if (!Helper.isNullOrWhitespace(contactName) && !signatureAttachment) {
            validationItems = [...validationItems,
            { key: "Signature", value: signature, required: true, type: Enums.ControlType.Custom }
            ];
        }

        if (Helper.isNullOrWhitespace(contactName) && signatureAttachment) {
            validationItems = [...validationItems,
            { key: "Contact", value: contactName, required: true, type: Enums.ControlType.Text },
            ];
        }

        const { isValid, errors } = Helper.validateInputs(validationItems);

        setInputErrors(errors);
        return isValid;
    };

    const deleteOldSignature = async () => {
        if (hasSignatureAttachment) {
            await AttachmentService.deleteAttachmentCustomerZone(c, t, tenant.API, signatureAttachment.ID);
        }
    };

    const saveSignature = async () => {
        await AttachmentService.saveSignatureCustomerZone(c, t, tenant.API, i, Enums.Module.JobCard, signature, false, null, toast);
    };

    const [updating, setUpdating] = useState(false);

    const updateSignature = async () => {
        setUpdating(true);
        if (signature) {
            await deleteOldSignature();
            await saveSignature();
            Helper.waitABit();
            getCustomerSignature();
            setShowSignatureOverlay(false);
            setUpdating(false);
            await submit();
        }
        else {
            toast.setToast!!!({ message: 'Please provide a signature', show: true, type: Enums.ToastType.error });
            setUpdating(false);
        }
    };

    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        setSubmitting(true);
        let isValid = validate();
        if (isValid) {
            job.SignatureName = contactName;
            await JobService.updateJobCustomerZone(c, t, tenant.API, job, toast);
            setShowSignatureOverlay(false);
            setShowThankYou(true);
            setSubmitting(false);
        } else {
            toast.setToast!!!({ message: 'There are errors on the page', show: true, type: Enums.ToastType.error });
            setSubmitting(false);
        }
    };

    const signatureOverlay = () => {
        return (
            <div className='signature-overlay'>
                <div className='signature-header'>
                    Signature for {contactName}
                </div>
                <NoSSR>
                    <div className='signature'>
                        <SignatureCanvas ref={signatureRef} onEnd={signatureCallback}
                            canvasProps={{ width: signatureCanvasWidth, height: signatureCanvasHeight, className: 'signatureCanvas' }} />
                    </div>
                </NoSSR>
                <div className='clear'>
                    <Button text="Clear" onClick={clearSignature} extraClasses="hollow w7" />
                </div>
                <div className='update'>
                    <Button text="Confirm" onClick={updateSignature} disabled={updating} extraClasses="w7" />
                </div>
                <div className='disclaimer'>
                    By signing you agree that the work is complete.
                </div>
                <div className='cancel'>
                    <Button text="Cancel" onClick={() => setShowSignatureOverlay(false)} extraClasses="hollow w7" />
                </div>
                <style jsx>{`
                    .signature-overlay { position: fixed; top: 0; left: 0; display: flex; flex-direction: column; background-color: ${colors.backgroundGrey}; width: 100%; height: 100%; z-index: 1; padding: 1rem; box-sizing: border-box; overflow-y: auto; }
                    .signature-header { display: flex; justify-content: center; font-weight: bold; }
                    .signature { display: flex; margin-top: 1rem; }
                    @media all and (orientation:portrait) {
                        .clear { position: absolute; bottom: 11rem; right: 1rem; }
                        .update { position: absolute; bottom: 8rem; right: 1rem; }
                        .disclaimer { position: absolute; bottom: 5rem; right: 1rem; text-align: right; width: 12rem; }
                        .cancel { position: absolute; bottom: 1rem; right: 1rem; }
                    }
                    @media all and (orientation:landscape) {
                        .clear { position: absolute; top: 1rem; right: 1rem; }
                        .update { position: absolute; top: 4rem; right: 1rem; }
                        .disclaimer { position: absolute; top: 9rem; right: 1rem; text-align: right; width: 150px; }
                        .cancel { position: absolute; bottom: 1rem; right: 1rem; }
                    }
                `}</style>
            </div>
        )
    };

    const thankYou = () => {
        return (
            <div className='signature-overlay'>
                <div className='signature-header'>
                    Signature for {job.JobCardNumber}
                </div>
                <div className='thankyou-message'>
                    Thank you for signing
                </div>
                <style jsx>{`
                    .signature-overlay { position: fixed; top: 0; left: 0; display: flex; flex-direction: column; background-color: ${colors.backgroundGrey}; width: 100%; height: 100%; z-index: 1; padding: 1rem; box-sizing: border-box; }
                    .signature-header { display: flex; justify-content: center; font-weight: bold; }
                    .thankyou-message { display: flex; justify-content: center; margin-top: 3rem; }
                `}</style>
            </div>
        )
    };

    if (!job || !tenant) return null;

    return (
        <NoSSR>
            <div className='signature-container'>
                <LeadMessage customerID={c} tenantID={t} page={"customerzone/signature"} />
                <div className='signature-header'>
                    Signature for {job.JobCardNumber}
                </div>
                <div className='signature-card'>
                    <div className='contact-name'>
                        <SCInput name="Contact" label="Customer" value={contactName} required={true} onChange={handleContactChange} error={inputErrors.Contact} />
                    </div>
                    <div className='sign-button'>
                        <Button text={`${hasSignatureAttachment ? 'Re-sign' : 'Sign'}`} onClick={signatureClick} extraClasses="fit-content no-margin" icon="pen-tool" />
                    </div>
                    {hasSignatureAttachment ?
                        <div className='current-signature'>
                            <img src={signatureAttachment.UrlThumb} className="thumb" />
                        </div> : ''
                    }
                </div>
            </div>

            {showSignatureOverlay ? signatureOverlay() : ''}
            {showThankYou ? thankYou() : ''}

            <style jsx>{`
                .signature-container { position: fixed; top: 0; left: 0; display: flex; flex-direction: column; background-color: ${colors.backgroundGrey}; width: 100%; height: 100%; z-index: 1; padding: 1rem; box-sizing: border-box; }
                .signature-header { display: flex; justify-content: center; font-weight: bold; }
                .signature-card { display: flex; flex-direction: column; padding: 1rem; box-shadow: ${shadows.card}; margin: 0 0 0.5rem 0; border-radius: 3px; position: relative; max-height: ${signatureCardMaxHeight}; }
                .thumb { max-height: ${thumbnailMaxHeight}; }
                .current-signature { display: flex; justify-content: center; }
                .sign-button { margin-top: 0.5rem; }
                .submit-button { display: flex; justify-content: flex-end; }
            `}</style>
        </NoSSR>
    )
}

export default Signature;

export const Route = createFileRoute('/customerzone/signature')({ component: Signature })
